import React from "react";
import { Chip, Tooltip, CircularProgress } from "@mui/material";
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Inventory as InventoryIcon,
} from "@mui/icons-material";
import { useProductCoverage } from "../../hooks/useDistributionCoverage";

export interface CoverageBadgeProps {
  unifiedProductId: string;
  enabled?: boolean;
  onClickDetail?: (productId: string) => void;
}

/**
 * Badge compacto que muestra cobertura por tienda del producto.
 * Click → abre StoreCoverageDialog con desglose.
 *
 * Estados visuales:
 *   🟢 OK            — todas las tiendas cubiertas, sin denials.
 *   🟡 con déficit   — al menos 1 tienda sin cobertura o N denials abiertos.
 *   🔴 crítico       — > 5 denials o > 30% tiendas sin cobertura.
 *   ⚪ skipped       — no hay datos de cálculo aún.
 */
export function CoverageBadge({
  unifiedProductId,
  enabled = true,
  onClickDetail,
}: CoverageBadgeProps): React.ReactElement {
  const { data, isLoading, isError } = useProductCoverage(
    unifiedProductId,
    enabled,
  );

  if (isLoading) {
    return (
      <Chip
        size="small"
        icon={<CircularProgress size={12} />}
        label="..."
        variant="outlined"
        sx={{ minWidth: 90 }}
      />
    );
  }

  if (isError || !data) {
    return (
      <Tooltip title="No se pudo calcular cobertura para este producto.">
        <Chip
          size="small"
          icon={<InventoryIcon fontSize="small" />}
          label="—"
          variant="outlined"
          sx={{ minWidth: 90 }}
        />
      </Tooltip>
    );
  }

  const totalStores =
    data.stores_covered.length + data.stores_uncovered.length;
  const uncoveredPct =
    totalStores === 0
      ? 0
      : (data.stores_uncovered.length / totalStores) * 100;
  const denials = data.pending_denials;

  const isCritical = denials > 5 || uncoveredPct > 30;
  const isWarning = !isCritical && (denials > 0 || data.stores_uncovered.length > 0);

  const status = isCritical ? "crítico" : isWarning ? "déficit" : "ok";
  const color: "success" | "warning" | "error" =
    status === "crítico" ? "error" : status === "déficit" ? "warning" : "success";

  const icon =
    status === "crítico" ? (
      <ErrorIcon fontSize="small" />
    ) : status === "déficit" ? (
      <WarningIcon fontSize="small" />
    ) : (
      <CheckIcon fontSize="small" />
    );

  const label =
    status === "ok"
      ? `${data.stores_covered.length}/${totalStores}`
      : `${data.stores_covered.length}/${totalStores} · ${denials}D`;

  const tooltip = [
    `Cobertura: ${data.stores_covered.length} de ${totalStores} tiendas activas.`,
    data.stores_uncovered.length > 0
      ? `Sin cobertura: ${data.stores_uncovered.join(", ")}.`
      : null,
    denials > 0
      ? `${denials} denegaciones pendientes (${data.pending_denials_qty.toFixed(0)} u).`
      : null,
    `Compra propuesta: ${data.total_qty_planned.toFixed(0)} u.`,
    `  Reposición: ${data.qty_replenishment_total.toFixed(0)}`,
    `  Muestras: ${data.qty_sample_total.toFixed(0)}`,
    `  Reorder: ${data.qty_reorder_total.toFixed(0)}`,
    `  Buffer central: ${data.qty_central_buffer.toFixed(0)}`,
    "Click para detalle por tienda.",
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <Tooltip title={<pre style={{ margin: 0 }}>{tooltip}</pre>}>
      <Chip
        size="small"
        icon={icon}
        label={label}
        color={color}
        variant="outlined"
        clickable={Boolean(onClickDetail)}
        onClick={
          onClickDetail ? () => onClickDetail(unifiedProductId) : undefined
        }
        sx={{ minWidth: 90, cursor: onClickDetail ? "pointer" : "default" }}
      />
    </Tooltip>
  );
}
